/**
 * R14.1-A — Keyholder two-pager.
 * Route: /keyholder (generic) OR /keyholder/:slug (personalized per-room).
 * Print-friendly via @media print.
 *
 * R15-5: When a slug is provided AND it matches a public seed room, the page:
 *   - title block changes to "A purpose-built broadcasting room for {Room Name}"
 *   - footer adds the room's community_handle_url as a link
 *   - testid `keyholder-page-personalized` with `data-slug` set
 *   - unknown slug falls back to the generic version (silently)
 */
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Printer, ExternalLink, LogIn, LayoutDashboard } from "lucide-react";
import { useAuth } from "../lib/auth-context";

const API = process.env.REACT_APP_BACKEND_URL;

export default function KeyholderBrief() {
  const params = useParams();
  const { user } = useAuth();
  const slug = (params?.slug || "").trim().toLowerCase();
  const [room, setRoom] = useState(null);  // null=unknown, false=loaded-no-match
  const onPrint = () => { try { window.print(); } catch { /* */ } };

  useEffect(() => {
    if (!slug) { setRoom(false); return; }
    let cancelled = false;
    fetch(`${API}/api/rooms/public`)
      .then((r) => r.json())
      .then((j) => {
        if (cancelled) return;
        const list = Array.isArray(j?.rooms) ? j.rooms : [];
        const hit = list.find(
          (r) => (r.slug || "").toLowerCase() === slug
        );
        setRoom(hit || false);
      })
      .catch(() => { if (!cancelled) setRoom(false); });
    return () => { cancelled = true; };
  }, [slug]);

  const personalized = !!(room && room.slug);
  const roomName = personalized ? room.name : "your community";
  const handleUrl = personalized ? room.community_handle_url : null;

  return (
    <div
      className="min-h-screen bg-[#050508] text-white"
      data-testid={personalized ? "keyholder-page-personalized" : "keyholder-page"}
      data-slug={personalized ? room.slug : undefined}
    >
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body, .kh-root { background: #fff !important; color: #111 !important; }
          .kh-page { box-shadow: none !important; border: none !important; page-break-after: always; padding: 28pt !important; }
          .kh-bullet { color: #111 !important; }
          .kh-divider { background: #888 !important; }
          h1, h2, h3 { color: #111 !important; }
          a { color: #1b6bd6 !important; }
        }
      `}</style>

      <header className="no-print sticky top-0 z-10 border-b border-white/10 bg-[#080810]/85 backdrop-blur">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#00F0FF]">Full Spectrum Center · Keyholder Brief</span>
          <div className="ml-auto flex items-center gap-2">
            {/* R16.1-C — Sign in / Dashboard link so Keyholders can return to login from the brief. */}
            {user ? (
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-[11px] uppercase tracking-[0.16em] font-bold bg-white/[0.05] hover:bg-white/[0.1] border border-white/12 text-white/75 hover:text-white"
                data-testid="keyholder-dashboard-link"
              >
                <LayoutDashboard className="w-3.5 h-3.5" />
                Go to dashboard
              </Link>
            ) : (
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-[11px] uppercase tracking-[0.16em] font-bold bg-white/[0.05] hover:bg-white/[0.1] border border-white/12 text-white/75 hover:text-white"
                data-testid="keyholder-signin-link"
              >
                <LogIn className="w-3.5 h-3.5" />
                Sign in
              </Link>
            )}
            <button
              onClick={onPrint}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-[11px] uppercase tracking-[0.16em] font-bold bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-[#0A0A0F]"
              data-testid="keyholder-print"
            >
              <Printer className="w-3.5 h-3.5" />
              Print / Save as PDF
            </button>
          </div>
        </div>
      </header>

      <main className="kh-root max-w-4xl mx-auto px-6 py-10">
        {/* Title */}
        <div className="kh-page rounded-2xl border border-white/10 bg-[#0a0a14] p-8 sm:p-10 mb-8">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#00F0FF] mb-3">A purpose-built broadcasting room</div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05]">Full Spectrum Center</h1>
          <p className="mt-3 text-base text-white/70 max-w-2xl" data-testid="keyholder-tagline">
            A purpose-built broadcasting room for <span className="font-bold text-white">{roomName}</span>.
          </p>
        </div>

        {/* Page 1 */}
        <section className="kh-page rounded-2xl border border-white/10 bg-[#0a0a14] p-8 sm:p-10 mb-8 space-y-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/45 mb-1">Page 1</div>
            <h2 className="text-2xl font-bold">What it is and what it does</h2>
          </div>

          <p className="text-sm text-white/85 leading-relaxed">
            <span className="font-bold">Full Spectrum Center</span> is a long-form audio + camera listening space designed
            specifically for community
            spaces that host live audio sets — DJ rooms, music nights, hangout streams, voice rooms. It is not a generic
            video conferencing tool retrofitted to your use case. It was built from scratch for what you actually do.
          </p>

          <Block label="For your participants">
            One-way audio broadcast from whoever is on the deck — a real DJ console, not a microphone share. Two-way text
            chat with rich formatting, emojis, GIFs, and short-lived file sharing (anything uploaded auto-deletes 10
            minutes after posting). Cameras stay on by default so the room feels like a room, not a podcast. Participants
            can flip a "Be Right Back" indicator when they step away. The interface mirrors the bottom-dock pattern people
            already know from Zoom, so onboarding is near-zero.
          </Block>

          <Block label="For you as Keyholder">
            Full control over a room that's yours. Promote co-hosts from within your existing community without making
            them set up new accounts. Hand off the deck cleanly between DJs. Lock the room when you want it private. Soft-close
            at end of night without dropping anyone abruptly. Configure programmable bots (more on page 2) that handle the
            moderation work you'd otherwise do manually.
          </Block>

          <Block label="For the DJs in your room">
            A full digital DJ console with five complexity tiers — from a basic 2-deck for beginners up to 4-deck
            performance pad mode for working DJs. The console runs in a pop-out window so the DJ can use a second monitor.
            Eight hot cues per deck, loop rolls from 1/32 to 4 beats, slip mode, EQ kill switches, a five-effect master rack
            (echo, reverb, delay, low-pass, high-pass), five mix transition styles, BPM and key detection, scrolling
            Serato/VDJ-style waveforms with stationary downbeat reference bars for beatmatching, and one-click Auto-DJ that
            can run a 2-hour set unattended. Tracks load from a folder on the DJ's own machine — no re-uploading every night.
            The full 2-hour mix can be recorded to the room or downloaded for the DJ's archive.
          </Block>

          <Block label="For everyone">
            A pre-room preview screen before entry so participants can pick the right camera, microphone, and speaker before
            anyone sees them. Devices can be swapped mid-room without disappearing from the gallery.
          </Block>
        </section>

        {/* Page 2 */}
        <section className="kh-page rounded-2xl border border-white/10 bg-[#0a0a14] p-8 sm:p-10 mb-8 space-y-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/45 mb-1">Page 2</div>
            <h2 className="text-2xl font-bold">Trust, cost, and how to get started</h2>
          </div>

          <Block label="What this costs you">
            Nothing. The platform is provided free to the 24 community Keyholders in this initial cohort. You receive
            Keyholder access to one room of your choice from the seeded list. You promote your own co-hosts from within
            your community.
          </Block>

          <Block label="What we ask in return">
            Be the room. Run the kind of community you've always run. Tell us when something breaks or could be better.
            That's the entire ask.
          </Block>

          <Block label="On trust and what we do with your data">
            This is a small platform built for niche communities. We don't run AI moderation that reads your members'
            messages — your room's moderation bots are rule-based, programmable by you, and never call out to an external
            language model with chat content. Camera presence detection runs on each participant's own machine and
            self-reports a one-bit status; the platform never sees the video pixels. Files shared in chat auto-delete after
            10 minutes. The DJ's set, if recorded, is saved to your room only — not to a public timeline. Co-host
            authentication uses Discord roles you already manage; we don't ask anyone for new credentials they don't need.
            We don't scrape, we don't resell, we don't profile. This is a community tool, not a growth-hack funnel.
          </Block>

          <div>
            <Label>Bots, programmable by you</Label>
            <p className="text-sm text-white/85 leading-relaxed mt-1">
              Each room ships with six bots, all toggleable on or off per room (default on), and each programmable by you with simple form-based rules — no coding required.
            </p>
            <ul className="mt-3 space-y-2 text-sm text-white/85">
              {BOT_BULLETS.map((b) => (
                <li key={b.name} className="kh-bullet leading-relaxed">
                  <span className="font-bold text-[#00F0FF]">{b.name}</span> — {b.desc}
                </li>
              ))}
            </ul>
            <p className="text-sm text-white/85 mt-3 leading-relaxed">
              Bots are silent to participants. They never appear in chat as a user. They never act without your sign-off
              when removal is involved.{" "}
              <a href="#bot-configuration" className="text-[#00F0FF] hover:underline" data-testid="keyholder-jump-bot-config">
                See full bot configuration ↓
              </a>
            </p>
          </div>

          <div>
            <Label>Getting started</Label>
            <ol className="mt-2 space-y-2 text-sm text-white/85 list-decimal pl-5">
              <li>Log in at the platform URL using the Discord account you'd like associated with your room.</li>
              <li>Find your room in the directory and confirm the community handle URL we have on file is correct (Telegram, Linktree, web link — whichever you provided).</li>
              <li>Configure your room's Discord bot for co-host authentication (5 minutes; we'll send setup instructions separately).</li>
              <li>Invite one or two cohosts from your community and promote them.</li>
              <li>Open the DJ console, load tracks from a local folder, and run a test set.</li>
            </ol>
          </div>

          <Block label="What's coming next">
            Native desktop apps for Windows and macOS with screen-recording protection so participants can't quietly capture
            the room. Native iOS and Android apps after that. Multi-pin gallery for rooms that want to feature multiple
            participants. Optional public livestream relay if you want to push a set beyond the room.
          </Block>

          <Block label="Support">
            Reply to whoever invited you, or open a chat with the platform team via the contact details shared in your
            onboarding email.
          </Block>
        </section>

        {/* R17-C — Bot Configuration. Friendly, second-person tone matching
            the existing brief. Anchor target for the page-2 short bots list. */}
        <section id="bot-configuration" className="kh-page rounded-2xl border border-white/10 bg-[#0a0a14] p-8 sm:p-10 mb-8 space-y-5" data-testid="keyholder-bot-configuration">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/45 mb-1">Bot configuration</div>
            <h2 className="text-2xl font-bold">How each bot works (and what you can change)</h2>
          </div>
          <p className="text-sm text-white/85 leading-relaxed">
            All six bots ship with sensible defaults so they work out of the box. To customize, open{" "}
            <span className="font-mono text-[#00F0FF]">Manage → Bots &amp; Rules</span> in your room.
          </p>

          <div>
            <Label>MessageBot</Label>
            <p className="text-sm text-white/85 mt-1 leading-relaxed">
              <span className="font-bold text-white">Default rule:</span> sends a welcome DM to every joining participant.
            </p>
            <p className="text-sm text-white/85 mt-2 leading-relaxed">
              <span className="font-bold text-white">You can:</span> change the message text, add scheduled announcements
              (e.g. "Room rules every 20 minutes"), set up chat commands (e.g.{" "}
              <span className="font-mono text-[#00F0FF]">!rules</span> → DM with rules).
            </p>
          </div>

          <div>
            <Label>CameraBot</Label>
            <p className="text-sm text-white/85 mt-1 leading-relaxed">
              <span className="font-bold text-white">Default behavior:</span> politely DMs participants whose camera has been
              off or shows no person for <span className="text-[#00F0FF] font-mono">60 seconds</span>. If they don't fix it
              within <span className="text-[#00F0FF] font-mono">180 more seconds (240 seconds total)</span>, the bot asks
              you for permission to remove them via a card in your Manage tray.
            </p>
            <p className="text-sm text-white/85 mt-2 leading-relaxed">
              <span className="font-bold text-white">You can:</span> adjust either timer, change the warning message, or
              disable for specific users (e.g. cohosts who legitimately step away).
            </p>
          </div>

          <div>
            <Label>NoSpamBot</Label>
            <p className="text-sm text-white/85 mt-1 leading-relaxed">
              <span className="font-bold text-white">Default behavior:</span> catches duplicate messages
              (<span className="text-[#00F0FF] font-mono">≥3 identical in 60 seconds</span>), rate-limit violations
              (<span className="text-[#00F0FF] font-mono">&gt;5 messages in 10 seconds</span>), and links from a
              banned-domain list. First-strike: mutes that user's chat input for{" "}
              <span className="text-[#00F0FF] font-mono">30 seconds</span>. After 3 strikes in 5 minutes: requests your
              approval to remove them.
            </p>
            <p className="text-sm text-white/85 mt-2 leading-relaxed">
              <span className="font-bold text-white">You can:</span> adjust thresholds, update the banned-domain list,
              change the strike count, or whitelist specific users (e.g. promoters posting legitimate event links).
            </p>
          </div>

          <div>
            <Label>NoSwarmBot</Label>
            <p className="text-sm text-white/85 mt-1 leading-relaxed">
              <span className="font-bold text-white">Default behavior:</span> detects coordinated raid posting (many new
              accounts posting identical or near-identical content within a short window). Surfaces a host-approval card.
            </p>
            <p className="text-sm text-white/85 mt-2 leading-relaxed">
              <span className="font-bold text-white">You can:</span> adjust the detection window and similarity threshold.
            </p>
          </div>

          <div>
            <Label>RenameBot</Label>
            <p className="text-sm text-white/85 mt-1 leading-relaxed">
              <span className="font-bold text-white">Default behavior:</span> flags display names containing banned words
              or impersonating staff (e.g. names with "admin", "moderator", or matching your own display name). DMs a
              polite rename request with a suggested alternative. After{" "}
              <span className="text-[#00F0FF] font-mono">3 minutes</span> without compliance: requests your approval to act.
            </p>
            <p className="text-sm text-white/85 mt-2 leading-relaxed">
              <span className="font-bold text-white">You can:</span> edit the banned-word list, adjust the response timeout,
              or add suggested alternatives.
            </p>
          </div>

          <div>
            <Label>AuthBot</Label>
            <p className="text-sm text-white/85 mt-1 leading-relaxed">
              <span className="font-bold text-white">Default behavior:</span> authenticates cohost promotions against your
              room's Discord role structure. Promotion only succeeds if the target user has the configured role.
            </p>
            <p className="text-sm text-white/85 mt-2 leading-relaxed">
              <span className="font-bold text-white">You need:</span> a Discord bot token for your room's server (we send
              setup instructions separately).
            </p>
          </div>

          <Block label="Renaming bots for your room">
            You can rename any bot to a friendlier identity (see the "Friendly bot names" section in Manage → Bots & Rules).
            Default names are functional ("MessageBot", "CameraBot"); custom names like "Welcome Bot" or "Camera Helper"
            feel more like community staff than software.
          </Block>

          <Block label="One rule">
            Every bot that wants to REMOVE a user asks for your permission first. You're always the final word. The bots
            can DM users, rate-limit chat, and surface cards — but they never act on a removal without you clicking Allow.
          </Block>
        </section>

        <footer className="kh-page rounded-2xl border border-white/10 bg-[#0a0a14] p-6 sm:p-8 text-center">
          <p className="italic text-sm text-white/65 leading-relaxed max-w-2xl mx-auto">
            Full Spectrum Center is built for communities like yours. Treat your room however you've always treated your space.
            We're here to give you the tools — you keep running the show.
          </p>
          {personalized && handleUrl && (
            <div className="mt-4 text-[11px] uppercase tracking-[0.22em] text-white/40">
              <span className="text-white/30 mr-2">Your community</span>
              <a
                href={handleUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#00F0FF] hover:text-white inline-flex items-center gap-1"
                data-testid="keyholder-community-handle-link"
              >
                {handleUrl.replace(/^https?:\/\//, "")} <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          )}
          <div className="mt-5 text-[10px] uppercase tracking-[0.22em] text-white/35">
            <a
              href={personalized ? `https://dj-room-poc.preview.emergentagent.com/keyholder/${room.slug}` : "https://dj-room-poc.preview.emergentagent.com/keyholder"}
              className="hover:text-white/65 inline-flex items-center gap-1"
            >
              {personalized
                ? `dj-room-poc.preview.emergentagent.com/keyholder/${room.slug}`
                : "dj-room-poc.preview.emergentagent.com/keyholder"}
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </footer>
      </main>
    </div>
  );
}

function Block({ label, children }) {
  return (
    <div>
      <Label>{label}</Label>
      <p className="text-sm text-white/85 mt-1 leading-relaxed">{children}</p>
    </div>
  );
}
function Label({ children }) {
  return <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#FF007F]">{children}</div>;
}

const BOT_BULLETS = [
  { name: "MessageBot",  desc: "posts predetermined messages on schedule, on participant join, or in response to commands. Set a welcome DM, periodic room rules, a \"!rules\" chat command. Useful, friendly, never punitive." },
  { name: "CameraBot",   desc: "politely DMs participants whose cameras are off or whose seats are empty, then asks you for permission before removing anyone. You're always the final word." },
  { name: "RenameBot",   desc: "flags bad display names with the same DM-then-ask-host pattern." },
  { name: "NoSpamBot",   desc: "catches duplicate floods and link spam, again with your approval before any removal." },
  { name: "NoSwarmBot",  desc: "catches coordinated raid posting and surfaces it for you to act on." },
  { name: "AuthBot",     desc: "gates co-host promotion through your Discord role structure." },
];
